/* eslint-disable max-lines */
import React, { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Popover, { type PopoverProps } from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { SimpleTreeViewProps } from "@mui/x-tree-view/SimpleTreeView";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { useTranslation } from "react-i18next";

import type { HierarchyPickerOption } from "./HierarchyPicker.types";
import {
  buildHierarchySearchMetadata,
  getHierarchySearchResult,
  getNextExpandedItems,
} from "./HierarchyPicker.utils";

export type HierarchyPickerProps<TOption extends HierarchyPickerOption = HierarchyPickerOption> = {
  options: TOption[];
  value: string[];
  onChange: (selectedIds: string[], selectedOptions: TOption[]) => void;
  defaultExpandedItems?: string[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  renderSelectedValue?: (selectedOptions: TOption[]) => string;
  getOptionId?: (option: TOption) => string;
  getOptionLabel?: (option: TOption) => string;
  getOptionChildren?: (option: TOption) => TOption[] | undefined;
  textFieldProps?: Omit<TextFieldProps, "value" | "onChange" | "select" | "multiline">;
  popoverProps?: Omit<PopoverProps, "open" | "anchorEl" | "onClose">;
};

const TREE_CONTAINER_MAX_HEIGHT_PX = 360;

const treeContainerSx = {
  maxHeight: TREE_CONTAINER_MAX_HEIGHT_PX,
  maxWidth: 480,
  minWidth: 320,
  overflow: "auto",
  p: 1,
};
const VIRTUALIZED_ROW_HEIGHT_PX = 36;
const VIRTUALIZED_OVERSCAN = 8;
const VIRTUALIZED_MIN_OPTION_COUNT = 120;
const MIN_SEARCH_QUERY_LENGTH = 2;
const HIDDEN_CHILD_PLACEHOLDER_PREFIX = "__hierarchy-placeholder__";

type SelectionState = { checked: boolean; indeterminate: boolean };

const HierarchyPicker = <TOption extends HierarchyPickerOption>({
  options,
  value,
  onChange,
  defaultExpandedItems,
  label,
  placeholder,
  disabled = false,
  size = "small",
  renderSelectedValue,
  getOptionId,
  getOptionLabel,
  getOptionChildren,
  textFieldProps,
  popoverProps,
}: HierarchyPickerProps<TOption>) => {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const treeContainerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation(["common"]);
  const resolvedPlaceholder = placeholder ?? t("common:select_options");
  const [scrollTop, setScrollTop] = useState(0);
  const [treeContainerViewportHeight, setTreeContainerViewportHeight] = useState(
    TREE_CONTAINER_MAX_HEIGHT_PX,
  );

  const optionIdGetter: (option: TOption) => string = useMemo(
    () => getOptionId ?? ((option: TOption) => option.id),
    [getOptionId],
  );
  const optionLabelGetter: (option: TOption) => string = useMemo(
    () => getOptionLabel ?? ((option: TOption) => option.label),
    [getOptionLabel],
  );
  const optionChildrenGetter: (option: TOption) => TOption[] | undefined = useMemo(
    () => getOptionChildren ?? ((option: TOption) => option.children as TOption[] | undefined),
    [getOptionChildren],
  );

  const rootIds = useMemo(() => options.map((option) => optionIdGetter(option)), [optionIdGetter, options]);
  const initialExpandedItems = useMemo(
    () => defaultExpandedItems ?? rootIds,
    [defaultExpandedItems, rootIds],
  );
  const [expandedItems, setExpandedItems] = useState<string[]>(initialExpandedItems);
  const [searchQuery, setSearchQuery] = useState("");
  const trimmedSearchQuery = searchQuery.trim();
  const hasSearchText = open && trimmedSearchQuery.length > 0;
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const trimmedDeferredSearchQuery = deferredSearchQuery.trim();
  const activeSearchQuery =
    trimmedDeferredSearchQuery.length >= MIN_SEARCH_QUERY_LENGTH ? deferredSearchQuery : "";
  const isSearchActive = open && activeSearchQuery.length > 0;

  useEffect(() => {
    if (open) return;
    setExpandedItems((current) => getNextExpandedItems(current, defaultExpandedItems, rootIds));
  }, [defaultExpandedItems, open, rootIds]);
  useEffect(() => {
    if (open || !searchQuery.length) {
      return;
    }

    setSearchQuery("");
  }, [open, searchQuery.length]);
  useEffect(() => {
    if (!open) {
      setTreeContainerViewportHeight(TREE_CONTAINER_MAX_HEIGHT_PX);
      return;
    }

    const container = treeContainerRef.current;
    if (!container) {
      setTreeContainerViewportHeight(TREE_CONTAINER_MAX_HEIGHT_PX);
      return;
    }

    const updateViewportHeight = () => {
      setTreeContainerViewportHeight(container.clientHeight || TREE_CONTAINER_MAX_HEIGHT_PX);
    };

    updateViewportHeight();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(updateViewportHeight);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    setScrollTop(0);
    if (treeContainerRef.current) treeContainerRef.current.scrollTop = 0;
  }, [activeSearchQuery, open, options]);

  const hierarchySearchMetadata = useMemo(
    () =>
      buildHierarchySearchMetadata(options, {
        getOptionChildren: optionChildrenGetter,
        getOptionId: optionIdGetter,
        getOptionLabel: optionLabelGetter,
      }),
    [optionChildrenGetter, optionIdGetter, optionLabelGetter, options],
  );

  const selectedIds = useMemo(() => new Set(value), [value]);
  const searchResult = useMemo(
    () =>
      getHierarchySearchResult(options, activeSearchQuery, hierarchySearchMetadata, {
        getOptionChildren: optionChildrenGetter,
        getOptionId: optionIdGetter,
        getOptionLabel: optionLabelGetter,
      }),
    [
      activeSearchQuery,
      hierarchySearchMetadata,
      optionChildrenGetter,
      optionIdGetter,
      optionLabelGetter,
      options,
    ],
  );
  const visibleOptions = useMemo(
    () => (isSearchActive ? searchResult.filteredOptions : options),
    [isSearchActive, options, searchResult.filteredOptions],
  );
  const effectiveExpandedItems = useMemo(
    () => (isSearchActive ? searchResult.expandedIds : expandedItems),
    [expandedItems, isSearchActive, searchResult.expandedIds],
  );
  const expandedItemSet = useMemo(
    () => (open ? new Set(effectiveExpandedItems) : new Set<string>()),
    [effectiveExpandedItems, open],
  );
  const hasExpandedRoot = useMemo(
    () => options.some((option) => expandedItemSet.has(optionIdGetter(option))),
    [expandedItemSet, optionIdGetter, options],
  );
  const useVirtualizedRootList = useMemo(
    // Virtualization only applies when no roots are expanded; once any root is
    // expanded the full tree is rendered so children scroll naturally.
    () => open && !isSearchActive && !hasExpandedRoot && options.length >= VIRTUALIZED_MIN_OPTION_COUNT,
    [hasExpandedRoot, isSearchActive, open, options.length],
  );

  const optionMap = hierarchySearchMetadata.optionMap;

  const mapIdsToOptions = useCallback(
    (ids: string[]): TOption[] => ids.map((id) => optionMap.get(id)).filter(Boolean) as TOption[],
    [optionMap],
  );
  const allOptionIds = useMemo(
    () => hierarchySearchMetadata.searchRecords.map((record) => record.id),
    [hierarchySearchMetadata.searchRecords],
  );
  const allOptionsSelected = useMemo(
    () => allOptionIds.length > 0 && allOptionIds.every((id) => selectedIds.has(id)),
    [allOptionIds, selectedIds],
  );

  const selectionStateById = useMemo(() => {
    const map = new Map<string, SelectionState>();
    const computeSelectionState = (option: TOption): SelectionState => {
      const id = optionIdGetter(option);
      const existing = map.get(id);
      if (existing) {
        return existing;
      }

      const children = optionChildrenGetter(option) ?? [];
      if (!children.length) {
        const leafState = { checked: selectedIds.has(id), indeterminate: false };
        map.set(id, leafState);
        return leafState;
      }

      let allChildrenChecked = true;
      let allChildrenFullyChecked = true;
      let someChildSelected = false;

      children.forEach((child) => {
        const childState = computeSelectionState(child);
        allChildrenChecked = allChildrenChecked && childState.checked;
        allChildrenFullyChecked = allChildrenFullyChecked && childState.checked && !childState.indeterminate;
        someChildSelected = someChildSelected || childState.checked || childState.indeterminate;
      });
      const selfSelected = selectedIds.has(id);

      const parentState = {
        checked: selfSelected || allChildrenChecked,
        indeterminate:
          (!(selfSelected || allChildrenChecked) && someChildSelected) ||
          ((selfSelected || allChildrenChecked) && someChildSelected && !allChildrenFullyChecked),
      };

      map.set(id, parentState);
      return parentState;
    };

    options.forEach(computeSelectionState);
    return map;
  }, [optionChildrenGetter, optionIdGetter, options, selectedIds]);

  const selectedOptions = useMemo(() => mapIdsToOptions(value), [mapIdsToOptions, value]);

  const displayValue = useMemo(() => {
    if (renderSelectedValue) return renderSelectedValue(selectedOptions);
    if (!selectedOptions.length) return "";

    const labels = selectedOptions.map((option) => optionLabelGetter(option));
    if (labels.length <= 3) {
      return t("common:hierarchy_picker_selection", { labels: labels.join(", ") });
    }

    return t("common:hierarchy_picker_selection_more", {
      labels: labels.slice(0, 3).join(", "),
      count: labels.length - 3,
    });
  }, [optionLabelGetter, renderSelectedValue, selectedOptions, t]);

  const collectIds = useCallback(
    function collect(option: TOption): string[] {
      const id = optionIdGetter(option);
      const children = optionChildrenGetter(option) ?? [];
      return [id, ...children.flatMap(collect)];
    },
    [optionChildrenGetter, optionIdGetter],
  );

  const getSelectionState = useCallback(
    (option: TOption): SelectionState => {
      const optionId = optionIdGetter(option);
      const selectionState = selectionStateById.get(optionId);
      if (selectionState) {
        return selectionState;
      }

      return { checked: false, indeterminate: false };
    },
    [optionIdGetter, selectionStateById],
  );

  const handleToggle = useCallback(
    (option: TOption, shouldSelect: boolean) => {
      const idsToUpdate = collectIds(option);
      const nextSelected = new Set(selectedIds);

      idsToUpdate.forEach((id) => {
        if (shouldSelect) {
          nextSelected.add(id);
        } else {
          nextSelected.delete(id);
        }
      });

      const nextSelectedIds = Array.from(nextSelected);
      const nextSelectedOptions = mapIdsToOptions(nextSelectedIds);

      onChange(nextSelectedIds, nextSelectedOptions);
    },
    [collectIds, mapIdsToOptions, onChange, selectedIds],
  );
  const selectTriggerText = useCallback(() => {
    const input = triggerRef.current?.querySelector("input, textarea");
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) {
      return;
    }

    requestAnimationFrame(() => {
      input.select();
    });
  }, []);

  const handleOpen = useCallback<NonNullable<TextFieldProps["onClick"]>>(
    (event) => {
      textFieldProps?.onClick?.(event);
      if (disabled) return;
      setOpen(true);
      selectTriggerText();
    },
    [disabled, selectTriggerText, textFieldProps],
  );
  const handleFocus = useCallback<NonNullable<TextFieldProps["onFocus"]>>(
    (event) => {
      textFieldProps?.onFocus?.(event);
      if (disabled) return;

      setOpen(true);
      selectTriggerText();
    },
    [disabled, selectTriggerText, textFieldProps],
  );
  const handleSearchChange = useCallback<NonNullable<TextFieldProps["onChange"]>>(
    (event) => {
      if (disabled) return;
      setOpen(true);
      setSearchQuery(event.target.value);
    },
    [disabled],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearchQuery("");
    setScrollTop(0);
    setExpandedItems((current) => getNextExpandedItems(current, defaultExpandedItems, rootIds));
  }, [defaultExpandedItems, rootIds]);

  const handleExpandedItemsChange = useCallback<
    Required<SimpleTreeViewProps<boolean>>["onExpandedItemsChange"]
  >(
    (_event, itemIds) => {
      if (isSearchActive) {
        return;
      }

      setExpandedItems(itemIds);
    },
    [isSearchActive],
  );
  const handleTreeContainerScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  }, []);

  const stopPropagation = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  const handleCheckboxChange = useCallback(
    (option: TOption) => (event: React.ChangeEvent<HTMLInputElement>) => {
      handleToggle(option, event.target.checked);
    },
    [handleToggle],
  );
  const handleSelectAll = useCallback(() => {
    const nextSelectedIds = allOptionIds;
    onChange(nextSelectedIds, mapIdsToOptions(nextSelectedIds));
  }, [allOptionIds, mapIdsToOptions, onChange]);
  const handleClearAll = useCallback(() => {
    onChange([], []);
  }, [onChange]);

  const renderTreeItems = useCallback(
    function renderItems(nodes: TOption[], forceFixedRootRowHeight = false): JSX.Element[] {
      return nodes.map((node) => {
        const id = optionIdGetter(node);
        const children = optionChildrenGetter(node) ?? [];
        const hasChildren = children.length > 0;
        const isExpanded = expandedItemSet.has(id);
        const { checked, indeterminate } = getSelectionState(node);
        const label = optionLabelGetter(node);

        return (
          <TreeItem
            key={id}
            itemId={id}
            slotProps={{ groupTransition: { timeout: 0, unmountOnExit: true } }}
            sx={
              forceFixedRootRowHeight
                ? {
                    "& > .MuiTreeItem-content": {
                      boxSizing: "border-box",
                      height: `${VIRTUALIZED_ROW_HEIGHT_PX}px`,
                    },
                    "& > .MuiTreeItem-content .MuiTreeItem-label": {
                      minWidth: 0,
                    },
                  }
                : undefined
            }
            label={
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ minWidth: 0, py: forceFixedRootRowHeight ? 0 : 0.5 }}
              >
                <Checkbox
                  size="small"
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={handleCheckboxChange(node)}
                  onClick={stopPropagation}
                />
                <Typography
                  variant="body2"
                  noWrap={forceFixedRootRowHeight}
                  title={forceFixedRootRowHeight ? label : undefined}
                  sx={forceFixedRootRowHeight ? { minWidth: 0 } : { wordBreak: "break-word" }}
                >
                  {label}
                </Typography>
              </Stack>
            }
          >
            {hasChildren
              ? isExpanded
                ? renderItems(children)
                : [
                    <TreeItem
                      key={`${HIDDEN_CHILD_PLACEHOLDER_PREFIX}${id}`}
                      itemId={`${HIDDEN_CHILD_PLACEHOLDER_PREFIX}${id}`}
                      label=""
                      sx={{ display: "none" }}
                    />,
                  ]
              : null}
          </TreeItem>
        );
      });
    },
    [
      expandedItemSet,
      getSelectionState,
      handleCheckboxChange,
      stopPropagation,
      optionChildrenGetter,
      optionIdGetter,
      optionLabelGetter,
    ],
  );

  const inputProps = {
    ...textFieldProps?.InputProps,
    endAdornment: textFieldProps?.InputProps?.endAdornment ?? <ArrowDropDownIcon color="action" />,
  };

  const virtualizationWindow = useMemo(() => {
    if (!useVirtualizedRootList) {
      return null;
    }

    const viewportHeight = treeContainerViewportHeight;
    const startIndex = Math.max(0, Math.floor(scrollTop / VIRTUALIZED_ROW_HEIGHT_PX) - VIRTUALIZED_OVERSCAN);
    const endIndex = Math.min(
      options.length,
      Math.ceil((scrollTop + viewportHeight) / VIRTUALIZED_ROW_HEIGHT_PX) + VIRTUALIZED_OVERSCAN,
    );

    return {
      startIndex,
      endIndex,
      topSpacerHeight: startIndex * VIRTUALIZED_ROW_HEIGHT_PX,
      bottomSpacerHeight: (options.length - endIndex) * VIRTUALIZED_ROW_HEIGHT_PX,
    };
  }, [options.length, scrollTop, treeContainerViewportHeight, useVirtualizedRootList]);

  const renderedTreeItems = useMemo(() => {
    if (!open) {
      return null;
    }

    if (!useVirtualizedRootList || !virtualizationWindow) {
      return renderTreeItems(visibleOptions);
    }

    const visibleRootOptions = visibleOptions.slice(
      virtualizationWindow.startIndex,
      virtualizationWindow.endIndex,
    );
    return renderTreeItems(visibleRootOptions, true);
  }, [open, renderTreeItems, useVirtualizedRootList, visibleOptions, virtualizationWindow]);

  return (
    <>
      <TextField
        {...textFieldProps}
        ref={triggerRef}
        label={label}
        placeholder={resolvedPlaceholder}
        value={hasSearchText ? searchQuery : displayValue}
        onChange={handleSearchChange}
        onClick={handleOpen}
        onFocus={handleFocus}
        fullWidth={textFieldProps?.fullWidth ?? true}
        size={size}
        disabled={disabled}
        InputProps={inputProps}
      />
      <Popover
        {...popoverProps}
        open={open}
        anchorEl={triggerRef.current}
        onClose={handleClose}
        disableAutoFocus={popoverProps?.disableAutoFocus ?? true}
        disableEnforceFocus={popoverProps?.disableEnforceFocus ?? true}
        disableRestoreFocus={popoverProps?.disableRestoreFocus ?? true}
        anchorOrigin={popoverProps?.anchorOrigin ?? { vertical: "bottom", horizontal: "left" }}
        transformOrigin={popoverProps?.transformOrigin ?? { vertical: "top", horizontal: "left" }}
      >
        <Stack direction="row" spacing={1} sx={{ px: 1, pt: 1 }}>
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={allOptionIds.length === 0 || allOptionsSelected}
          >
            {t("common:select_all")}
          </Button>
          <Button size="small" onClick={handleClearAll} disabled={value.length === 0}>
            {t("common:clear_all")}
          </Button>
        </Stack>
        <Box ref={treeContainerRef} sx={treeContainerSx} onScroll={handleTreeContainerScroll}>
          <SimpleTreeView
            defaultExpandedItems={initialExpandedItems}
            expandedItems={effectiveExpandedItems}
            onExpandedItemsChange={handleExpandedItemsChange}
            slots={{ expandIcon: ChevronRightIcon, collapseIcon: ExpandMoreIcon }}
            aria-label={label ?? t("common:hierarchy_picker")}
            sx={
              useVirtualizedRootList && virtualizationWindow
                ? {
                    pt: `${virtualizationWindow.topSpacerHeight}px`,
                    pb: `${virtualizationWindow.bottomSpacerHeight}px`,
                  }
                : undefined
            }
          >
            {renderedTreeItems}
          </SimpleTreeView>
        </Box>
      </Popover>
    </>
  );
};

export default memo(HierarchyPicker) as typeof HierarchyPicker;
